import React from 'react';
import { Container } from 'react-bootstrap';
import AnimateOnScroll from '../components/AnimateOnScroll';
import './Downloads.css';
import { FaAndroid } from 'react-icons/fa';
import pulmoscanLogo from '../assets/logo-pulmoscan.png';
import pulmoflowLogo from '../assets/logo-pulmoflow.png';

/** PulmoScan: edite `public/downloads/version.json` */
const publicUrl = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
const VERSION_JSON_PATH = `${publicUrl}/downloads/version.json`;
/** PulmoFlow: edite `public/downloads/pulmoflow_version.json` (APK: `pulmoflow_<versão>.apk`) */
const PULMOFLOW_VERSION_JSON_PATH = `${publicUrl}/downloads/pulmoflow_version.json`;

const FALLBACK_PULMOSCAN_VERSION = '3.0.1';
const FALLBACK_PULMOFLOW_VERSION = '1.0.0';
const FALLBACK_APK_SIZE_LABEL = '100MB';

const formatApkSizeLabel = (data) => {
  const mb = data?.size_mb ?? data?.pulmoscan_size_mb ?? data?.pulmoflow_size_mb;
  if (mb != null && mb !== '') {
    const n = Number(mb);
    return Number.isFinite(n) ? `${n}MB` : String(mb);
  }
  return FALLBACK_APK_SIZE_LABEL;
};

const Downloads = () => {
  const [expandedApp, setExpandedApp] = React.useState(null);
  const [closingApp, setClosingApp] = React.useState(null);
  const [pulmoscanVersion, setPulmoscanVersion] = React.useState(null);
  const [pulmoscanSizeLabel, setPulmoscanSizeLabel] = React.useState(null);
  const [versionLoadState, setVersionLoadState] = React.useState('idle'); // idle | loading | ok | error
  const [pulmoflowVersion, setPulmoflowVersion] = React.useState(null);
  const [pulmoflowSizeLabel, setPulmoflowSizeLabel] = React.useState(null);
  const [pulmoflowLoadState, setPulmoflowLoadState] = React.useState('idle');

  React.useEffect(() => {
    let cancelled = false;
    setVersionLoadState('loading');
    fetch(VERSION_JSON_PATH, { cache: 'no-store' })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        const v = data?.version != null ? String(data.version).trim() : '';
        if (v) setPulmoscanVersion(v.replace(/^v/i, ''));
        setPulmoscanSizeLabel(formatApkSizeLabel(data));
        setVersionLoadState('ok');
      })
      .catch(() => {
        if (cancelled) return;
        setPulmoscanVersion(FALLBACK_PULMOSCAN_VERSION);
        setPulmoscanSizeLabel(FALLBACK_APK_SIZE_LABEL);
        setVersionLoadState('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    setPulmoflowLoadState('loading');
    fetch(PULMOFLOW_VERSION_JSON_PATH, { cache: 'no-store' })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        const v = data?.version != null ? String(data.version).trim() : '';
        if (v) setPulmoflowVersion(v.replace(/^v/i, ''));
        setPulmoflowSizeLabel(formatApkSizeLabel(data));
        setPulmoflowLoadState('ok');
      })
      .catch(() => {
        if (cancelled) return;
        setPulmoflowVersion(FALLBACK_PULMOFLOW_VERSION);
        setPulmoflowSizeLabel(FALLBACK_APK_SIZE_LABEL);
        setPulmoflowLoadState('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const displayVersion =
    versionLoadState === 'loading' && !pulmoscanVersion ? '…' : pulmoscanVersion || FALLBACK_PULMOSCAN_VERSION;

  const displayPulmoflowVersion =
    pulmoflowLoadState === 'loading' && !pulmoflowVersion ? '…' : pulmoflowVersion || FALLBACK_PULMOFLOW_VERSION;

  const handleCardClick = (appId) => {
    if (expandedApp === appId) {
      setClosingApp(appId);
      setTimeout(() => {
        setExpandedApp(null);
        setClosingApp(null);
      }, 350);
    } else {
      setClosingApp(null);
      setExpandedApp(appId);
    }
  };

  const handleDownload = (appSlug, version) => {
    const slug = String(appSlug).toLowerCase().replace(/\s+/g, '_');
    const fileName = `${slug}_${version}.apk`;
    const link = document.createElement('a');
    const base = publicUrl || '';
    link.href = `${base}/downloads/${fileName}`;
    link.download = fileName;
    link.type = 'application/vnd.android.package-archive';
    
    // Adiciona atributos para forçar download como APK
    link.setAttribute('data-downloadurl', ['application/vnd.android.package-archive', link.download, link.href].join(':'));
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <section className="downloads-section">
      <Container>
        <AnimateOnScroll variant="fadeUp">
          <div className="downloads-header">
            <h1>Downloads</h1>
            <p>Inove na saúde! Baixe nossas soluções e faça a diferença.</p>
          </div>
        </AnimateOnScroll>

        <AnimateOnScroll variant="fadeUp" delay={100}>
        <div className="apps-list">
          {/* PulmoScan App */}
          <div className={`app-item-wrapper ${expandedApp === 'pulmoscan' ? 'expanded' : ''}`}>
            <div 
              className={`download-card ${expandedApp === 'pulmoscan' ? 'active' : ''}`}
              onClick={() => handleCardClick('pulmoscan')}
            >
              <div className="available-badge">
                Acesso Disponível
              </div>
              <div className="app-icon">
                <img src={pulmoscanLogo} alt="PulmoScan Logo" />
              </div>
              <div className="app-preview-content">
                <h2>PulmoScan</h2>
                <p>Aplicativo profissional de avaliação respiratória com IA e PulmoGame integrados</p>
                <div className="version-info">
                  <span>Versão: {displayVersion}</span>
                  <span>Tamanho: {pulmoscanSizeLabel || FALLBACK_APK_SIZE_LABEL}</span>
                  <span className="platform-badge-inline">
                    <FaAndroid className="platform-icon-inline" /> Android
                  </span>
                </div>
              </div>
            </div>

            {expandedApp === 'pulmoscan' && (
              <div className={`app-details-wrapper ${closingApp === 'pulmoscan' ? 'closing' : ''}`}>
                <div className="app-details-row">
                  <div className="app-details-column">
                    <div className="download-details">
                      <h3>Recursos</h3>
                      <ul>
                        <li>Cadastro e gestão de pacientes e fisioterapeutas</li>
                        <li>Avaliações de PImáx e PEmáx com normalização</li>
                        <li>Relatórios inteligentes e exportação em PDF</li>
                        <li>PulmoGame integrado para engajamento no treinamento</li>
                        <li>Armazenamento de dados local e na nuvem</li>
                        <li>Compatível somente com Android</li>
                      </ul>
                    </div>
                  </div>
                  <div className="app-details-column">
                    <div className="requirements">
                      <h3>Requisitos</h3>
                      <ul>
                        <li>Android 8.0 ou superior</li>
                        <li>100MB de espaço livre</li>
                        <li>Bluetooth 4.0+</li>
                      </ul>
                    </div>
                    <div className="download-actions">
                      <button 
                        className="download-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload('pulmoscan', pulmoscanVersion || '3.0.1');
                        }}
                      >
                        <i className="fas fa-download"></i> Baixar APK
                      </button>
                    </div>
                  </div>
                </div>

                {/* Tutorial dentro do card expandido */}
                <div className="tutorial-wrapper">
                  <div className="tutorial">
                    <h3 id="tutorial-top">PulmoScan — Guia de Instalação e Uso</h3>
                    <div className="tutorial-index">
                      <a href="#tutorial-instalacao">Instalação</a>
                      <a href="#tutorial-api">Configuração da API</a>
                      <a href="#tutorial-conectividade">Conectividade</a>
                      <a href="#tutorial-uso">Uso</a>
                      <a href="#tutorial-relatorios">Relatórios</a>
                      <a href="#tutorial-configuracoes">Configurações</a>
                    </div>
                    <div className="tutorial-steps">
                      <h4 id="tutorial-instalacao">Instalação</h4>
                      <ol>
                        <li>
                          <strong>Download e Instalação:</strong>
                          <ol type="a">
                            <li>Baixe o arquivo "PulmoScan.apk" nesta página.</li>
                            <li>No Android, habilite a instalação de apps de fontes desconhecidas quando solicitado.</li>
                            <li>Abra o instalador, aceite os termos de uso e LGPD, e conclua a instalação.</li>
                            <li>Abra o PulmoScan após a instalação.</li>
                          </ol>
                        </li>
                      </ol>

                      <h4 id="tutorial-api">Configuração da Chave da API (Gemini)</h4>
                      <ol>
                        <li>
                          <strong>Obter a Chave:</strong>
                          <ol type="i">
                            <li>Acesse <a href="https://makersuite.google.com/app/apikey" target="_blank" rel="noopener noreferrer">Google AI Studio</a>.</li>
                            <li>Crie/login em uma conta Google, se necessário.</li>
                            <li>Clique em "Create API Key" e copie a chave gerada.</li>
                          </ol>
                        </li>
                        <li>
                          <strong>Inserir no PulmoScan:</strong>
                          <ol type="i">
                            <li>Na tela inicial, toque no ícone de engrenagem (⚙️) ao lado do Bluetooth.</li>
                            <li>Localize o campo "Gemini API" e cole a sua chave.</li>
                            <li>Toque em "Salvar"; a validação ocorre automaticamente.</li>
                          </ol>
                        </li>
                      </ol>

                      <h4 id="tutorial-conectividade">Conectividade com o BTOne</h4>
                      <ol>
                        <li>
                          <strong>Conectar o Dispositivo:</strong>
                          <ol type="a">
                            <li>Ative o Bluetooth do seu celular/tablet.</li>
                            <li>No PulmoScan, toque no ícone de Bluetooth em vermelho.</li>
                            <li>Selecione o dispositivo BTOne (prefixo HXXQE...).</li>
                            <li>Conectado: o ícone ficará verde e o status será atualizado.</li>
                          </ol>
                        </li>
                      </ol>

                      <h4 id="tutorial-uso">Uso do PulmoScan</h4>
                      <ol>
                        <li>
                          <strong>Home (Ações Rápidas):</strong>
                          <ol type="a">
                            <li>Sessão: iniciar avaliação ou treinamento (Game).</li>
                            <li>Gerenciar Paciente: cadastrar/editar/excluir pacientes.</li>
                            <li>Gerenciar Fisioterapeuta: cadastrar/editar/excluir fisioterapeutas.</li>
                            <li>Relatórios: ver histórico e gerar PDF.</li>
                          </ol>
                        </li>
                        <li>
                          <strong>Criando Sessão:</strong>
                          <ol type="a">
                            <li>Selecione Paciente, Fisioterapeuta e Tipo da Sessão (Avaliação ou Game).</li>
                          </ol>
                        </li>
                        <li>
                          <strong>Avaliação:</strong>
                          <ol type="a">
                            <li>Tela mostra manômetro digital e gráfico em tempo real.</li>
                            <li>Toque em "iniciar avaliação" para iniciar e novamente para parar.</li>
                            <li>Após parar: refazer ou salvar (salvar e ir para Relatórios).</li>
                          </ol>
                        </li>
                        <li>
                          <strong>Treinamento (Game):</strong>
                          <ol type="a">
                            <li>Escolha o jogo (ex.: PulmoPlane), nível de dificuldade e duração.</li>
                            <li>Ao terminar: Jogar novamente, Salvar (vai para Relatórios) ou Sair.</li>
                          </ol>
                        </li>
                        <li>
                          <h4 id="tutorial-relatorios">Relatórios</h4>
                          <ol type="a">
                            <li>Acesse pela Home (Relatórios) ou após salvar uma sessão.</li>
                            <li>Para exportar, use "Gerar PDF" (primeira vez) e depois "Abrir PDF".</li>
                          </ol>
                        </li>
                        <li>
                          <h4 id="tutorial-configuracoes">Configurações</h4>
                          <ol type="a">
                            <li>Perfil do usuário (dados e senha), Gemini API, suporte/contato e termos.</li>
                          </ol>
                        </li>
                      </ol>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* PulmoFlow — primeira versão (testes) */}
          <div className={`app-item-wrapper ${expandedApp === 'pulmoflow' ? 'expanded' : ''}`}>
            <div
              className={`download-card ${expandedApp === 'pulmoflow' ? 'active' : ''}`}
              onClick={() => handleCardClick('pulmoflow')}
            >
              <div className="available-badge available-badge--beta">
                Versão de testes
              </div>
              <div className="app-icon">
                <img src={pulmoflowLogo} alt="PulmoFlow Logo" />
              </div>
              <div className="app-preview-content">
                <h2>PulmoFlow</h2>
                <p>
                  Aplicativo para pacientes coletarem pico de fluxo, responder questionários ACT/CAT e enviar
                  amostras aos médicos via ecossistema BTTwo
                </p>
                <div className="version-info">
                  <span>Versão: {displayPulmoflowVersion}</span>
                  <span>Tamanho: {pulmoflowSizeLabel || FALLBACK_APK_SIZE_LABEL}</span>
                  <span className="platform-badge-inline">
                    <FaAndroid className="platform-icon-inline" /> Android
                  </span>
                </div>
              </div>
            </div>

            {expandedApp === 'pulmoflow' && (
              <div className={`app-details-wrapper ${closingApp === 'pulmoflow' ? 'closing' : ''}`}>
                <div className="app-details-row">
                  <div className="app-details-column">
                    <div className="download-details">
                      <h3>Recursos</h3>
                      <ul>
                        <li>Testes de pico de fluxo (PEF) com gamificação</li>
                        <li>Questionários ACT e CAT integrados</li>
                        <li>Histórico de sessões e envio de dados ao profissional</li>
                        <li>Pensado para uso com o medidor BTTwo (Bluetooth)</li>
                        <li>Compatível somente com Android</li>
                      </ul>
                    </div>
                  </div>
                  <div className="app-details-column">
                    <div className="requirements">
                      <h3>Requisitos</h3>
                      <ul>
                        <li>Android 8.0 ou superior (recomendado)</li>
                        <li>100MB de espaço livre</li>
                        <li>Bluetooth 4.0+ para conexão com BTTwo</li>
                      </ul>
                    </div>
                    <div className="download-actions">
                      <button
                        type="button"
                        className="download-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload('pulmoflow', pulmoflowVersion || FALLBACK_PULMOFLOW_VERSION);
                        }}
                      >
                        <i className="fas fa-download" /> Baixar APK ({pulmoflowVersion || FALLBACK_PULMOFLOW_VERSION})
                      </button>
                    </div>
                  </div>
                </div>

                <div className="tutorial-wrapper">
                  <div className="tutorial">
                    <h3 id="pulmoflow-tutorial-top">PulmoFlow — Instalação (testes)</h3>
                    <div className="tutorial-steps">
                      <ol>
                        <li>
                          Baixe o arquivo{' '}
                          <strong>
                            pulmoflow_{pulmoflowVersion || FALLBACK_PULMOFLOW_VERSION}.apk
                          </strong>{' '}
                          nesta página.
                        </li>
                        <li>
                          No Android, permita instalação de apps de fontes desconhecidas, se o sistema solicitar.
                        </li>
                        <li>Instale e abra o PulmoFlow. Esta build é para testes; relatórios de uso ajudam a evoluir o app.</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        </AnimateOnScroll>
      </Container>
    </section>
  );
};

export default Downloads; 